import type { CSSProperties } from 'react'
import { useState } from 'react'
import {
  Tag as AriaTag,
  Disclosure,
  DisclosurePanel,
  TagGroup,
  TagList,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { tv } from 'tailwind-variants'
import IcBaselineExpandLess from '~icons/ic/baseline-expand-less'
import IcBaselineExpandMore from '~icons/ic/baseline-expand-more'
import IcBaselineRemove from '~icons/ic/baseline-remove'
import MediaPreview from '../Common/MediaPreview'
import { UIButton } from '../ui/Button'
import { UITooltipTriggerWrapper } from '../ui/Tooltip'
import type { DetailedTag, TagItem } from './utils'

const TagChipVariant = tv({
  defaultVariants: {
    variant: 'selection',
    size: 'medium',
  },
  slots: {
    tag: 'rounded-full ',
    button: 'rounded-full ',
    media: 'rounded-full ',
    icon: 'rounded-full ',
  },
  variants: {
    variant: {
      selection: {
        tag: 'bg-gray-100 dark:bg-gray-700',
        icon: 'text-black dark:text-white bg-gray-300 dark:bg-gray-500',
      },
      recommendation: {},
    },
    size: {
      small: {
        button: 'text-xs py-0.5 px-1 gap-1',
        media: 'size-3',
        icon: 'size-3',
      },
      medium: {
        button: 'text-sm py-1 px-2 gap-2',
        media: 'size-4',
        icon: 'size-4',
      },
      large: {
        button: 'text-md py-2 px-4 gap-3',
        media: 'size-5',
        icon: 'size-5',
      },
    },
    category: {
      contest: {},
      feature: {},
      category: {},
      other: {},
    },
    type: {
      add: {},
      remove: {},
    },
    disabled: {
      true: {
        button:
          'opacity-40 cursor-not-allowed text-gray-500 !outline-gray-400 dark:!outline-gray-600',
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      category: ['contest', 'feature', 'category', 'other'],
      type: 'add',
      variant: 'recommendation',
      className: {
        button: 'outline outline-1',
      },
    },
    {
      category: ['contest', 'feature', 'category', 'other'],
      type: 'remove',
      variant: 'recommendation',
      className: {
        button: 'outline outline-2',
      },
    },
    {
      category: 'contest',
      variant: 'selection',
      className: {
        button: 'text-orange-500',
      },
    },
    {
      category: 'contest',
      variant: 'recommendation',
      className: {
        button:
          'text-[var(--contest-accent-light)] outline-[var(--contest-accent-light)] dark:text-[var(--contest-accent-dark)] dark:outline-[var(--contest-accent-dark)]',
      },
    },
    {
      category: 'category',
      variant: 'recommendation',
      className: {
        button: 'text-purple-500 outline-purple-500',
      },
    },
    {
      category: 'feature',
      variant: 'recommendation',
      className: {
        button: 'text-green-500 outline-green-500',
      },
    },
  ],
})

export type TagChipProps = {
  type: 'add' | 'remove'
  tag: DetailedTag
  onAdd: (tag: TagItem) => void
  onRemove: (tag: TagItem) => void
  variant?: 'selection' | 'recommendation'
  category?: 'contest' | 'feature' | 'category' | 'other'
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
}

export const TagChip = ({
  type,
  tag,
  onAdd,
  onRemove,
  variant,
  size,
  category,
  disabled = false,
}: TagChipProps) => {
  const { t } = useTranslation(['artwork'])

  const handleClick = (tag: TagItem) => {
    if (type === 'add') {
      onAdd(tag)
    } else if (type === 'remove') {
      onRemove(tag)
    }
  }

  const styles = TagChipVariant({
    variant,
    size,
    category,
    type,
    disabled,
  })

  const contestButtonStyle = tag.contest
    ? ({
        '--contest-accent-light': tag.contest.lightAccentColor,
        '--contest-accent-dark': tag.contest.darkAccentColor,
      } as CSSProperties)
    : {}

  const tagButton = (
    <UIButton
      slot="remove"
      variant="ghost"
      onPress={() => handleClick(tag.tag)}
      className={styles.button()}
      style={contestButtonStyle}
      size="large"
      disabled={disabled}
      icon={
        tag.tag.mediaId && (
          <MediaPreview className={styles.media()} mediaId={tag.tag.mediaId} />
        )
      }
      endIcon={
        // display delete icon when tag is selected
        variant === 'selection' && type === 'remove' ? (
          <IcBaselineRemove className={styles.icon()} />
        ) : null
      }
    >
      {tag.name}
    </UIButton>
  )

  if (!tag.contest) {
    return <AriaTag className={styles.tag()}>{tagButton}</AriaTag>
  }

  return (
    <AriaTag className={styles.tag()}>
      <UITooltipTriggerWrapper placement="top" delay={100} trigger={tagButton}>
        <span className="p-2">{t('artwork:join-contest.click-to-join')}</span>
      </UITooltipTriggerWrapper>
    </AriaTag>
  )
}

export type TagChipGroupProps = {
  chips: Omit<TagChipProps, 'size'>[]
  size?: 'small' | 'medium' | 'large'
  foldLimit?: number
}

export const TagChipGroup = ({ chips, size, foldLimit }: TagChipGroupProps) => {
  const [expanded, setExpanded] = useState(false)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const shouldFold = foldLimit && chips.length > foldLimit
  const visibleChips = shouldFold ? chips.slice(0, foldLimit) : chips
  const hiddenChips = shouldFold ? chips.slice(foldLimit) : []

  if (!shouldFold) {
    return (
      <TagGroup>
        <TagList className="flex flex-wrap gap-2">
          {chips.map(item => (
            <TagChip key={item.tag.name} {...item} size={size} />
          ))}
        </TagList>
      </TagGroup>
    )
  }

  return (
    <>
      <Disclosure isExpanded={expanded}>
        <TagGroup>
          <TagList className="flex flex-wrap gap-2">
            {visibleChips.map(item => (
              <TagChip key={item.tag.name} {...item} size={size} />
            ))}
          </TagList>
        </TagGroup>
        <DisclosurePanel className="pt-2">
          <TagGroup>
            <TagList className="flex flex-wrap gap-2">
              {hiddenChips.map(item => (
                <TagChip key={item.tag.name} {...item} size={size} />
              ))}
            </TagList>
          </TagGroup>
        </DisclosurePanel>
      </Disclosure>
      <UIButton variant="ghost" onPress={toggleExpanded} size="normal">
        {expanded ? (
          <span className="flex justify-center items-center h-full">
            <IcBaselineExpandLess className="size-6 " />
          </span>
        ) : (
          <span className="flex justify-center items-center h-full">
            <IcBaselineExpandMore className="size-6" />
          </span>
        )}
      </UIButton>
    </>
  )
}
